// src/views/kiddo-games/pairing/CardToPair.tsx
// Kopia src/components/Pairing/CardToPair.tsx — tylko dla Sparuj Easy
import React, {
  FC, useEffect, useRef, memo,
} from 'react';
import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import useKeepOnBoard from '../../../hooks/useKeepOnBoard';
import classes from './CardToPair.module.less';
import boardClasses from './KiddoPairingView.module.less';
import getCounterStripBottom from './counterStrip';

gsap.registerPlugin(Draggable);

type CardProps = {
  id: string | number;
  content: string;
  onMatch: (id: string | number) => void;
  isPaired: boolean;
  isMatching: boolean;
};

export const CardToPair: FC<CardProps> = memo(({
  content,
  id,
  onMatch,
  isPaired,
  isMatching,
}: CardProps) => {
  const cardId = `card-${id}`;
  const draggableRef = useRef<Draggable[] | null>(null);
  const elementRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);

  // Po zmianie rozmiaru planszy karta przesuwa się proporcjonalnie i nie wypada za krawędź ani na pas z licznikiem plansz
  useKeepOnBoard(elementRef, getCounterStripBottom);

  useEffect(() => {
    const cardElement = elementRef.current;
    if (!cardElement || isPaired || initializedRef.current) {
      return undefined;
    }

    setTimeout(() => {
      const boardElement = document.querySelector(`.${boardClasses.boardForPics}`);
      if (!boardElement) return;

      const boardRect = boardElement.getBoundingClientRect();
      const maxX = boardRect.width - cardElement.offsetWidth - 20;
      const maxY = boardRect.height - cardElement.offsetHeight - 20;
      // Pod pasem z licznikiem plansz, żeby karta go nie zasłoniła
      const minY = Math.max(20, getCounterStripBottom());

      const randomX = 20 + Math.random() * (maxX - 20);
      const randomY = minY + Math.random() * (maxY - minY);

      const centerX = boardRect.width / 2 - cardElement.offsetWidth / 2;
      const centerY = boardRect.height / 2 - cardElement.offsetHeight / 2;

      // 🎯 Ustaw style PRZED dodaniem klasy
      cardElement.style.position = 'absolute';
      cardElement.style.left = `${centerX}px`;
      cardElement.style.top = `${centerY}px`;
      cardElement.style.transform = 'scale(0.3) rotate(0deg)';

      // 🎯 Dodaj klasę, żeby CSS przestał ukrywać
      cardElement.classList.add(classes.initialized);

      // 🎯 ANIMACJA ROZRZUCENIA
      gsap.to(cardElement, {
        left: randomX,
        top: randomY,
        opacity: 1,
        scale: 1,
        rotation: () => gsap.utils.random(-5, 5),
        duration: 0.8,
        delay: Math.random() * 0.4,
        ease: 'back.out(1.4)',
      });

      const handleDragEnd = function handleDragEnd(this: Draggable) {
        const draggableElement = elementRef.current;
        const pictureElement = document.getElementById(`picture-${id}`);

        if (!draggableElement || !pictureElement) return;

        const cardRect = draggableElement.getBoundingClientRect();
        const pictureRect = pictureElement.getBoundingClientRect();

        const overlapX = Math.max(0,
          Math.min(cardRect.right, pictureRect.right)
        - Math.max(cardRect.left, pictureRect.left));
        const overlapY = Math.max(0,
          Math.min(cardRect.bottom, pictureRect.bottom)
        - Math.max(cardRect.top, pictureRect.top));

        const cardArea = cardRect.width * cardRect.height;
        const overlapArea = overlapX * overlapY;
        const overlapPercentage = (overlapArea / cardArea) * 100;

        if (overlapPercentage > 30) {
          if (draggableRef.current) {
            draggableRef.current.forEach((instance) => instance.disable());
          }
          onMatch(id);
        }
      };

      gsap.delayedCall(1.2, () => {
        draggableRef.current = Draggable.create(`#${cardId}`, {
          type: 'x,y',
          edgeResistance: 0.65,
          onDragEnd: handleDragEnd,
        });
      });

      initializedRef.current = true;
    }, 100);

    return () => {
      if (draggableRef.current) {
        draggableRef.current.forEach((instance) => instance.kill());
        draggableRef.current = null;
      }
    };
  }, [cardId, id, onMatch, isPaired]);

  // ✅ PROSTY SHRINK
  useEffect(() => {
    if (!isMatching || !elementRef.current) {
      return undefined;
    }

    const element = elementRef.current;

    gsap.to(element, {
      scale: 0,
      opacity: 0,
      duration: 0.3,
      ease: 'power2.in',
    });

    return undefined;
  }, [isMatching]);

  if (isPaired) return null;

  return (
    <div
      ref={elementRef}
      className={classes.cardtopair}
      id={cardId}
    >
      {content}
    </div>
  );
});

export default CardToPair;
